import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { CookieSettingsProvider } from "@/components/CookieSettings";
import ErrorBoundary from "@/components/ErrorBoundary";
import ProtectedRoute from "@/components/ProtectedRoute";

import BusinessDemo from "@/pages/BusinessDemo";
import Home from "@/pages/Home";
import CompanyInfoPage from "@/pages/CompanyInfoPage";
import AboutPage from "@/pages/AboutPage";
import Modules from "@/pages/Modules";
import SupplyChainModulePage from "@/pages/modules/SupplyChain";
import ManufacturingModulePage from "@/pages/modules/Manufacturing";
import CompliancePublicPage from "@/pages/modules/Compliance";
import ZynthoroVsOdoo from "@/pages/vs/ZynthoroVsOdoo";
import ZynthoroVsNetSuite from "@/pages/vs/ZynthoroVsNetSuite";
import AssistantsPage from "@/pages/AssistantsPage";
import PricingPage from "@/pages/PricingPage";
import AffiliatePage from "@/pages/AffiliatePage";
import WebsiteAndDomains from "@/pages/WebsiteAndDomains";
import Signup from "@/pages/Signup";
import Login from "@/pages/Login";
import VerifyEmail from "@/pages/VerifyEmail";
import TwoFactorSetup from "@/pages/TwoFactorSetup";
import TwoFactorVerify from "@/pages/TwoFactorVerify";
import Onboarding from "@/pages/Onboarding";
import SubscribeStarter from "@/pages/SubscribeStarter";
import SubscribeStarterReturn from "@/pages/SubscribeStarterReturn";
import SubscribeTier from "@/pages/SubscribeTier";
import ClaimAccount from "@/pages/ClaimAccount";
import SubscribeReturn from "@/pages/SubscribeReturn";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import DashboardLayout from "@/pages/DashboardLayout";
import DashboardHome from "@/pages/dashboard/DashboardHome";
import ModulePlaceholder from "@/pages/dashboard/ModulePlaceholder";
import HRModule from "@/pages/dashboard/HRModule";
import AccountingModule from "@/pages/dashboard/AccountingModule";
import CommunicationModule from "@/pages/dashboard/CommunicationModule";
import CommunicationOutboundPage from "@/pages/dashboard/CommunicationOutboundPage";
import AIStudioDraftsPage from "@/pages/dashboard/AIStudioDraftsPage";
import ComplianceModule from "@/pages/dashboard/ComplianceModule";
import FinanceModule from "@/pages/dashboard/FinanceModule";
import SalesModule from "@/pages/dashboard/SalesModule";
import ProjectsModule from "@/pages/dashboard/ProjectsModule";
import PlanningModule from "@/pages/dashboard/PlanningModule";
import SchedulingModule from "@/pages/dashboard/SchedulingModule";
import TimeTrackingModule from "@/pages/dashboard/TimeTrackingModule";
import TeamPage from "@/pages/dashboard/Team";
import Settings from "@/pages/dashboard/Settings";
import AssistantPage from "@/pages/dashboard/AssistantPage";
import MarketingContent from "@/pages/dashboard/MarketingContent";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import TermsOfService from "@/pages/legal/TermsOfService";
import CookiePolicy from "@/pages/legal/CookiePolicy";
import DPA from "@/pages/legal/DPA";
import SLA from "@/pages/legal/SLA";
import BlogIndex from "@/pages/blog/BlogIndex";
import BlogPost from "@/pages/blog/BlogPost";
import NotFound from "@/pages/NotFound";
import AIStudio from "@/pages/dashboard/AIStudio";
import MetaCallback from "@/pages/dashboard/MetaCallback";
import SocialAccountCallback from "@/pages/dashboard/SocialAccountCallback";
import DomainsPage from "@/pages/dashboard/DomainsPage";
import WebsiteBuilderPage from "@/pages/dashboard/WebsiteBuilderPage";
import MeetingGuestPage from "@/pages/MeetingGuestPage";
import RecordingsPage from "@/pages/dashboard/RecordingsPage";
import MeetingsCalendarPage from "@/pages/dashboard/MeetingsCalendarPage";
import CreditsPage from "@/pages/dashboard/CreditsPage";
import RevenuePage from "@/pages/dashboard/RevenuePage";
import useCanonical from "@/lib/useCanonical";
import useRouteScroll from "@/lib/useRouteScroll";
import usePosthogRouteGuard from "@/lib/usePosthogRouteGuard";

function CanonicalSync() {
  useCanonical();
  useRouteScroll();
  usePosthogRouteGuard();
  return null;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <CookieSettingsProvider>
          <BrowserRouter>
          <CanonicalSync />
          <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/modules" element={<Modules />} />
            <Route path="/modules/supply-chain" element={<SupplyChainModulePage />} />
            <Route path="/modules/manufacturing" element={<ManufacturingModulePage />} />
            <Route path="/modules/compliance" element={<CompliancePublicPage />} />
            <Route path="/assistants" element={<AssistantsPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/vs/zynthoro-vs-odoo" element={<ZynthoroVsOdoo />} />
            <Route path="/vs/zynthoro-vs-netsuite" element={<ZynthoroVsNetSuite />} />
            <Route path="/affiliate" element={<AffiliatePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/careers" element={<CompanyInfoPage kind="careers" />} />
            <Route path="/press" element={<CompanyInfoPage kind="press" />} />
            <Route path="/website" element={<WebsiteAndDomains />} />
            <Route path="/demo" element={<ProtectedRoute><BusinessDemo /></ProtectedRoute>} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/login" element={<Login />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/claim-account" element={<ClaimAccount />} />
            <Route path="/meetings/join/:eventId" element={<MeetingGuestPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/2fa/setup" element={<TwoFactorSetup />} />
            <Route path="/2fa/verify" element={<TwoFactorVerify />} />
            <Route path="/subscribe/starter" element={<SubscribeStarter />} />
            <Route path="/subscribe/starter/return" element={<SubscribeStarterReturn />} />
            <Route path="/subscribe/return" element={<SubscribeReturn />} />
            <Route path="/subscribe/:tierKey" element={<SubscribeTier />} />
            <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/legal/terms-of-service" element={<TermsOfService />} />
            <Route path="/legal/cookie-policy" element={<CookiePolicy />} />
            <Route path="/legal/dpa" element={<DPA />} />
            <Route path="/legal/sla" element={<SLA />} />
            {/* Backwards-compatible redirects for the earlier short URLs */}
            <Route path="/legal/privacy" element={<Navigate to="/legal/privacy-policy" replace />} />
            <Route path="/legal/terms" element={<Navigate to="/legal/terms-of-service" replace />} />
            <Route path="/legal/cookies" element={<Navigate to="/legal/cookie-policy" replace />} />

            {/* Blog (public — articles ingested from Outrank webhook) */}
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route
              path="/onboarding"
              element={
                <ProtectedRoute>
                  <Onboarding />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardHome />} />
              <Route path="team" element={<TeamPage />} />
              <Route path="settings" element={<Settings />} />
              <Route path="domains" element={<DomainsPage />} />
              <Route path="website-builder" element={<WebsiteBuilderPage />} />
              <Route path="meetings" element={<MeetingsCalendarPage />} />
              <Route path="recordings" element={<RecordingsPage />} />
              <Route path="credits" element={<CreditsPage />} />
              <Route path="revenue" element={<RevenuePage />} />
              <Route path="finance-admin" element={<RevenuePage />} />
              <Route path="zyntha" element={<AssistantPage assistantKey="zyntha" />} />
              <Route path="thoro" element={<AssistantPage assistantKey="thoro" />} />
              <Route path="zyona" element={<AssistantPage assistantKey="zyona" />} />
              <Route path="marketing" element={<MarketingContent />} />
              <Route path="hr" element={<HRModule />} />
              <Route path="accounting" element={<AccountingModule />} />
              <Route path="communication" element={<CommunicationModule />} />
              <Route path="communication/outbound" element={<CommunicationOutboundPage />} />
              <Route path="compliance" element={<ComplianceModule />} />
              <Route path="finance" element={<FinanceModule />} />
              <Route path="sales" element={<SalesModule />} />
              <Route path="projects" element={<ProjectsModule />} />
              <Route path="planning" element={<PlanningModule />} />
              <Route path="scheduling" element={<SchedulingModule />} />
              <Route path="time-tracking" element={<TimeTrackingModule />} />
              <Route path="ai-studio" element={<AIStudio />} />
              <Route path="ai-studio/drafts" element={<AIStudioDraftsPage />} />
              <Route path="meta-callback" element={<MetaCallback />} />
              <Route path="social-callback/:provider" element={<SocialAccountCallback />} />
              <Route path=":slug" element={<ModulePlaceholder />} />
            </Route>
            {/* SEO: branded 404 for unknown routes.
                NOTE — the SPA cannot override the HTTP status the static
                host already emitted (200). The host must be configured to
                return HTTP 404 for unknown routes; see public/_headers,
                public/_redirects and the "flagged for founder" note in the
                SEO changelog. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </ErrorBoundary>
        </BrowserRouter>
        <Toaster richColors position="top-center" />
        </CookieSettingsProvider>
      </AuthProvider>
    </div>
  );
}

export default App;

